// #3185 APPROVE.2 PR 2c — one layout for the three client approval matrices
// (Settings → Client approvals, the project override panel, the Partner
// Portal default). Rows grouped by phase; each screen supplies the control
// and the source hint for a row.

import type { ReactNode } from 'react';
import { clientApprovalTypeLabel, groupByPhase, typeNote } from '../../lib/clientApprovals';

interface Props {
  types: readonly string[];
  renderControl: (type: string) => ReactNode;
  hint?: (type: string) => ReactNode;
  testId?: string;
}

const phaseStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.05em', padding: '14px 0 6px',
};
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
  padding: '10px 0', borderTop: '1px solid var(--border)',
};

export default function ClientApprovalMatrixTable({ types, renderControl, hint, testId }: Props) {
  return (
    <div data-testid={testId}>
      {groupByPhase(types).map((phase) => (
        <section key={phase.key} aria-label={phase.label}>
          <div style={phaseStyle}>{phase.label}</div>
          {phase.types.map((type) => {
            const note = typeNote(type);
            return (
              <div key={type} style={rowStyle} data-testid={`matrix-row-${type}`}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)' }}>
                    {clientApprovalTypeLabel(type)}
                  </div>
                  {(hint || note) && (
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {hint?.(type)}
                      {hint && note ? ' · ' : null}
                      {note}
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0 }}>{renderControl(type)}</div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
